// src/components/layout/MenuItem.tsx
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as S from './Sidebar.styles';
import { MenuItemType } from './sidebar.data';
import { FiChevronRight } from 'react-icons/fi';

interface Props {
  item: MenuItemType;
  isSidebarOpen: boolean;
}

const renderMenuIcon = (item: MenuItemType) => {
  if (item.iconComponent) {
    const IconComponent = item.iconComponent;
    return <IconComponent className="icon" />;
  }

  if (item.icon) {
    const Icon = item.icon;
    return <Icon className="icon" />;
  }

  return null;
};

const MenuItem: React.FC<Props> = ({ item, isSidebarOpen }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const hasChildren = !!item.children?.length;
  const isActive =
    pathname === item.path || pathname.startsWith(`${item.path}/`);
  const [subOpen, setSubOpen] = useState(isActive);

  useEffect(() => {
    setSubOpen(isActive);
  }, [isActive, pathname]);

  const handleParentClick = () => {
    if (isSidebarOpen) {
      setSubOpen((prev) => !prev);
      return;
    }

    navigate(item.path);
    setSubOpen(true);
  };

  if (hasChildren) {
    return (
      <S.NavItemContainer
        className={`has-children${isActive ? ' active-parent' : ''}`}
      >
        <S.MenuItemButton
          onClick={handleParentClick}
          isActive={isActive}
          title={!isSidebarOpen ? item.name : undefined}
        >
          {renderMenuIcon(item)}
          {isSidebarOpen && <span className="item-text">{item.name}</span>}
          <FiChevronRight
            className="arrow-icon"
            style={{
              transform: subOpen && isSidebarOpen ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
          />
        </S.MenuItemButton>

        {isSidebarOpen ? (
          <S.SubMenu isOpen={subOpen}>
            {item.children?.map((c) => (
              <S.SubMenuItem
                key={c.path}
                to={c.path}
                className={pathname === c.path ? 'active' : ''}
              >
                {renderMenuIcon(c)}
                <span className="item-text">{c.name}</span>
              </S.SubMenuItem>
            ))}
          </S.SubMenu>
        ) : (
          <S.FlyoutSubMenu className="flyout-submenu">
            {item.children?.map((c) => (
              <S.FlyoutSubMenuItem
                key={c.path}
                to={c.path}
                className={pathname === c.path ? 'active' : ''}
              >
                {renderMenuIcon(c)}
                <span className="item-text">{c.name}</span>
              </S.FlyoutSubMenuItem>
            ))}
          </S.FlyoutSubMenu>
        )}
      </S.NavItemContainer>
    );
  }

  return (
    <S.NavItemContainer>
      <S.NavItemLink
        to={item.path}
        end
        title={isSidebarOpen ? undefined : item.name}
      >
        {renderMenuIcon(item)}
        {isSidebarOpen && <span className="item-text">{item.name}</span>}
      </S.NavItemLink>
    </S.NavItemContainer>
  );
};

export default MenuItem;
